import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Save, FileText } from 'lucide-react';
import AxiosDB from '@/config/axios2';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BillingInfo = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    tipo_comprobante: 'Factura',
    rnc: '',
    nombre: '',
    direccion: '',
    telefonos: ''
  });

  useEffect(() => {
    fetchBillingInfo();
  }, []);

  const fetchBillingInfo = async () => {
    try {
      const response = await AxiosDB.get(`/billing-info`);
      if (response.data) {
        setFormData(response.data);
      }
    } catch (error) {
      console.error('Error fetching billing info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await AxiosDB.put(`/billing-info`, formData);
      toast.success('Información de facturación actualizada');
    } catch (error) {
      toast.error('Error al actualizar la información');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]">Cargando...</div>;
  }

  return (
    <div className="space-y-6" data-testid="billing-info-page">
      <div>
        <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Información de Facturación
        </h2>
        <p className="text-gray-600">Configura tus datos para la emisión de facturas</p>
      </div>

      <Card className="shadow-lg">
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-purple-600" />
            <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Datos de Facturación</CardTitle>
          </div>
          <CardDescription>
            Esta información será utilizada en todas tus facturas
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="tipo_comprobante" data-testid="tipo-comprobante-label">
                  Tipo de Comprobante *
                </Label>
                <Select
                  value={formData.tiponcf}
                  onValueChange={(value) => handleChange('tiponcf', value)}
                >
                  <SelectTrigger data-testid="tipo-comprobante-select">
                    <SelectValue placeholder="Selecciona" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Factura de Consumo">Factura de Consumo</SelectItem>
                    <SelectItem value="Comprobante Fiscal">Comprobante Fiscal</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="rnc" data-testid="rnc-label">
                  RNC *
                </Label>
                <Input
                  id="rnc"
                  data-testid="rnc-input"
                  placeholder="123-4567890-1"
                  value={formData.rnc}
                  onChange={(e) => handleChange('rnc', e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="nombre" data-testid="nombre-label">
                  Nombre o Razón Social *
                </Label>
                <Input
                  id="nombre"
                  data-testid="nombre-input"
                  placeholder="Nombre completo o empresa"
                  value={formData.nombre}
                  onChange={(e) => handleChange('nombre', e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="direccion" data-testid="direccion-label">
                  Dirección *
                </Label>
                <Input
                  id="direccion"
                  data-testid="direccion-input"
                  placeholder="Calle, número, ciudad"
                  value={formData.direccion}
                  onChange={(e) => handleChange('direccion', e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="telefono" data-testid="telefono-label">
                  Teléfono *
                </Label>
                <Input
                  id="telefono"
                  data-testid="telefono-input"
                  type="tel"
                  placeholder="+1 (809) 555-1234"
                  value={formData.telefonos}
                  onChange={(e) => handleChange('telefonos', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                data-testid="save-button"
                disabled={saving}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  fontWeight: 600
                }}
              >
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Guardando...' : 'Guardar Información'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingInfo;